import React from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import useApi from '../hooks/useApi';
import { PROGRAMS } from '../constants/programs';
import ConfirmDialog from './ConfirmDialog';
import EmailSendDetailDialog from './emailAlerts/EmailSendDetailDialog';
import EmailSendFilters from './emailAlerts/EmailSendFilters';
import EmailSendContextFilters from './emailAlerts/EmailSendContextFilters';
import EmailSendTable from './emailAlerts/EmailSendTable';
import useEmailSendReportState from '../hooks/useEmailSendReportState';
import EmailSendManualDialog from './emailAlerts/EmailSendManualDialog';
import EventEmailOperationDialog from './emailAlerts/EventEmailOperationDialog';

const EmailsSentReport = () => {
  const {
    useEmailTriggerSends,
    useEmailTriggerSendFilterKeys,
    useEmailTriggerSendDetail,
    useDeleteEmailTriggerSend,
    useRetryEmailTriggerSend,
    useRefreshEmailTriggerSend,
    useSendManualEmailTrigger,
    useEmailTriggers,
    useRunCronJob,
  } = useApi();

  const {
    constants,
    state,
    setters,
    derived,
    actions,
  } = useEmailSendReportState();

  const { data, isLoading, error, isFetching, refetch } = useEmailTriggerSends(derived.queryPayload);
  const { data: filterKeyData } = useEmailTriggerSendFilterKeys({
    start: derived.centralRange.start,
    end: derived.centralRange.end,
  });
  const { data: detail, isLoading: detailLoading } = useEmailTriggerSendDetail(state.selectedId);
  const deleteSend = useDeleteEmailTriggerSend();
  const retrySend = useRetryEmailTriggerSend();
  const refreshSend = useRefreshEmailTriggerSend();
  const sendManualEmail = useSendManualEmailTrigger();
  const { data: triggerData, isLoading: triggerLoading } = useEmailTriggers();
  const runCronJob = useRunCronJob();
  const [runConfirmOpen, setRunConfirmOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);
  const [manualBac, setManualBac] = React.useState('');
  const [manualProgram, setManualProgram] = React.useState('');
  const [manualTriggerId, setManualTriggerId] = React.useState('');
  const [manualError, setManualError] = React.useState('');
  const [eventOperationState, setEventOperationState] = React.useState({
    row: null,
    operation: null,
  });

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const rowCount = Number.isFinite(Number(data?.totalRows)) ? Number(data.totalRows) : rows.length;
  const isTruncated = Boolean(data?.truncated);
  const filterKeys = Array.isArray(filterKeyData?.keys) ? filterKeyData.keys : [];
  const allTriggers = Array.isArray(triggerData) ? triggerData : [];
  const triggerOptions = allTriggers
    .filter((trigger) => trigger?.isActive !== false)
    .filter((trigger) => !manualProgram || !trigger?.programName || trigger.programName === manualProgram)
    .sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || '')));
  const programOptions = PROGRAMS.map((program) => ({ value: program, label: program }));

  const confirmDelete = async () => {
    if (!state.confirmState.id) return;
    try {
      await deleteSend.mutateAsync(state.confirmState.id);
      setters.setConfirmState({ open: false, id: null });
      if (state.selectedId === state.confirmState.id) setters.setSelectedId(null);
      refetch();
    } catch (_error) {
      setters.setConfirmState({ open: false, id: null });
    }
  };

  const confirmRetry = async () => {
    if (!state.retryState.id) return;
    try {
      await retrySend.mutateAsync({ id: state.retryState.id, options: { forceSend: true } });
      setters.setRetryState({ open: false, id: null });
      refetch();
    } catch (_error) {
      setters.setRetryState({ open: false, id: null });
    }
  };

  const refreshSelected = async () => {
    if (!state.selectedId) return;
    try {
      await refreshSend.mutateAsync({ id: state.selectedId });
      refetch();
    } catch (_error) {
      // noop
    }
  };

  const resetManualForm = () => {
    setManualBac('');
    setManualProgram('');
    setManualTriggerId('');
    setManualError('');
  };

  const closeManualDialog = () => {
    setManualOpen(false);
    resetManualForm();
  };

  const openManualDialog = () => {
    setManualError('');
    setManualOpen(true);
  };

  const submitManualEmail = async () => {
    if (!manualBac || !manualProgram || !manualTriggerId) return;
    setManualError('');
    try {
      await sendManualEmail.mutateAsync({
        bac: manualBac,
        programName: manualProgram,
        triggerId: manualTriggerId,
      });
      closeManualDialog();
      refetch();
    } catch (error) {
      setManualError(error?.message || 'Failed to send manual email.');
    }
  };

  const confirmRunEmailTriggers = async () => {
    try {
      await runCronJob.mutateAsync('nightly-email-triggers');
      setRunConfirmOpen(false);
    } catch (_error) {
      setRunConfirmOpen(false);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Emails Sent"
        subheader="Email trigger send log with context filtering."
        action={(
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openManualDialog}
            >
              Add email
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={() => setRunConfirmOpen(true)}
              disabled={runCronJob.isPending}
            >
              {runCronJob.isPending ? 'Starting…' : 'Run email triggers'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        )}
      />
      <CardContent>
          <Stack spacing={2}>
            <EmailSendFilters
              programName={state.programName}
              status={state.status}
              bac={state.bac}
              search={state.search}
              startDate={state.startDate}
              endDate={state.endDate}
              programOptions={[constants.ALL_PROGRAMS, ...PROGRAMS]}
              statusOptions={constants.STATUS_OPTIONS}
              isTruncated={isTruncated}
              onProgramChange={setters.setProgramName}
              onStatusChange={setters.setStatus}
              onBacChange={setters.setBac}
              onSearchChange={setters.setSearch}
              onStartDateChange={setters.setStartDate}
              onEndDateChange={setters.setEndDate}
              onResetPage={() => setters.setPaginationModel((prev) => ({ ...prev, page: 0 }))}
            />

            <EmailSendContextFilters
              filterRows={state.filterRows}
              filterKeys={filterKeys}
              onUpdateRow={actions.updateFilterRow}
              onRemoveRow={actions.removeFilterRow}
              onAddRow={() => setters.setFilterRows((prev) => [...prev, actions.buildFilterRow()])}
              onApply={actions.applyFilters}
              onClear={actions.clearFilters}
            />

            {error && <Alert severity="error">{error?.message || 'Failed to load email sends.'}</Alert>}
            {!isLoading && rows.length === 0 && <Alert severity="info">No email sends found for this range.</Alert>}

            <EmailSendTable
              rows={rows}
              rowCount={rowCount}
              paginationModel={state.paginationModel}
              onPaginationModelChange={setters.setPaginationModel}
              loading={isLoading}
              isFetching={isFetching}
              onView={(id) => setters.setSelectedId(id)}
              onRetry={(id) => setters.setRetryState({ open: true, id })}
              onDelete={(id) => setters.setConfirmState({ open: true, id })}
              onEventOperation={(row, operation) =>
                setEventOperationState({ row, operation })
              }
            />
          </Stack>
      </CardContent>

      <EmailSendDetailDialog
        open={Boolean(state.selectedId)}
        detail={detail}
        loading={detailLoading}
        onClose={() => setters.setSelectedId(null)}
        onDelete={() => setters.setConfirmState({ open: true, id: state.selectedId })}
        onRefresh={refreshSelected}
        onRetry={() => setters.setRetryState({ open: true, id: state.selectedId })}
        onEventOperation={(row, operation) =>
          setEventOperationState({ row, operation })
        }
        refreshing={refreshSend.isPending}
        retrying={retrySend.isPending}
        showDelete={detail?.genericActions?.delete !== false}
        showRetry={detail?.genericActions?.retry !== false}
        showRefresh={detail?.genericActions?.refresh !== false}
        eventOperation={
          detail?.eventOperations?.resend
            ? 'resend'
            : detail?.eventOperations?.retry
              ? 'retry'
              : null
        }
      />

      <EventEmailOperationDialog
        row={eventOperationState.row}
        operation={eventOperationState.operation}
        onClose={() =>
          setEventOperationState({ row: null, operation: null })
        }
        onCompleted={() => refetch()}
      />

      <EmailSendManualDialog
        open={manualOpen}
        bac={manualBac}
        programName={manualProgram}
        triggerId={manualTriggerId}
        triggerOptions={triggerOptions}
        programOptions={programOptions}
        loadingTriggers={triggerLoading}
        submitting={sendManualEmail.isPending}
        error={manualError}
        onBacChange={setManualBac}
        onProgramChange={(value) => {
          setManualProgram(value);
          setManualTriggerId('');
        }}
        onTriggerChange={setManualTriggerId}
        onClose={closeManualDialog}
        onSubmit={submitManualEmail}
      />

      <ConfirmDialog
        open={state.confirmState.open}
        title="Delete email log?"
        description="This will permanently remove the log entry."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteSend.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setters.setConfirmState({ open: false, id: null })}
      />

      <ConfirmDialog
        open={state.retryState.open}
        title="Retry email send?"
        description="This will re-render and send the email again."
        confirmLabel="Retry"
        confirmColor="primary"
        loading={retrySend.isPending}
        onConfirm={confirmRetry}
        onCancel={() => setters.setRetryState({ open: false, id: null })}
      />

      <ConfirmDialog
        open={runConfirmOpen}
        title="Run email triggers now?"
        description="WARNING: This job could take several hours, are you sure you want to continue?"
        confirmLabel="Proceed"
        confirmColor="warning"
        loading={runCronJob.isPending}
        onConfirm={confirmRunEmailTriggers}
        onCancel={() => setRunConfirmOpen(false)}
      />
    </Card>
  );
};

export default EmailsSentReport;
